import type { ReactNode } from 'react'
import { useState } from 'react'

import LaunchIcon from '@mui/icons-material/Launch'
import LinkOff from '@mui/icons-material/LinkOff'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import Realisatie from '@components/Realisatie'
import StyledTable from '@components/StyledTable'

import { useAuthenticatedUser } from '../../auth/hooks/useAuthenticatedUser'
import { StyledExpandCard } from '../../theme/components/StyledExpandCard'
import { StyledIconButton } from '../../theme/components/StyledIconButton'
import { StyledIconLink } from '../../theme/components/StyledIconLink'
import { useLinkedProjects } from '../hooks/useLinkedProjects'

interface Props {
  entity: any
  linkedProjectIds: string[]
  isVerantwoordelijke: boolean
  isAannemer?: boolean
  toolbar?: ReactNode
  unlinkHandler: (id: string, unlinkId: string) => void
}

const headerCells = [
  { id: 'aanvraagID', label: 'Project ID' },
  { id: 'uitvoerdatum', label: 'Geplande start uitvoering' },
  { id: 'doorlooptijdWeken', label: 'Doorlooptijd (in weken)' },
  { id: 'netbeheerders', label: 'Netbeheerders' },
  { id: 'aannemers', label: 'Aannemers' },
  { id: 'statusText', label: 'Status' },
  { id: 'acties', label: 'Acties', fixed: 100 },
]

export const GenericLinkedProjects = ({
  entity,
  linkedProjectIds,
  isVerantwoordelijke,
  isAannemer,
  toolbar,
  unlinkHandler,
}: Props) => {
  const {
    data: linkedProjects,
    isError,
    isLoading,
  } = useLinkedProjects({
    linkedProjectIds,
  })

  const [open, setOpen] = useState(false)
  const [unlinkId, setUnlinkId] = useState('')

  const user = useAuthenticatedUser()

  const isHoofdleiding = entity.projecttype === 'Hoofdleiding'
  const canKoppelProject =
    user.canKoppelProject && (isVerantwoordelijke || isAannemer)
  const filteredHeaderCells = isHoofdleiding
    ? headerCells.filter((h) => h.id !== 'doorlooptijdWeken')
    : headerCells

  const handleUnlinkClick = (childId: string) => {
    setUnlinkId(childId)
    setOpen(true)
  }

  const handleUnlink = () => {
    unlinkHandler(entity.id, unlinkId)
    setOpen(false)
  }

  return (
    <StyledExpandCard title="Gekoppelde projecten" buttonBar={toolbar}>
      <StyledTable
        headerCells={filteredHeaderCells}
        variant="details"
        isError={isError}
        isLoading={isLoading}
        {...({} as any)}
      >
        {linkedProjects &&
          linkedProjects.map((p) => {
            if (typeof p === 'object') {
              /**
               * Deze check is om te controleren of het linkedProject (`p`) is gevuld met een veld dat,
               * als de partij geauthorizeerd is, altijd gevuld is.
               * Als deze leeg is komt hij via de `explodeKnownProjects` er niet in (zie project util),
               * en kunnen we zeggen dat hij unauthorized is
               */
              const isAuthorizedOnProject = Object.keys(p).includes('combi')

              return (
                <TableRow key={p.aanvraagID}>
                  <TableCell>{p.aanvraagID}</TableCell>
                  <TableCell>
                    {p.geplandeUitvoeringsdatum ? (
                      <Realisatie
                        date={p.geplandeUitvoeringsdatum as string}
                        type={p.geplandeUitvoeringsdatumType}
                      />
                    ) : (
                      p.startUitvoeringWeek
                    )}
                  </TableCell>
                  {!isHoofdleiding && (
                    <TableCell>{p.doorlooptijdWeken}</TableCell>
                  )}
                  <TableCell>{p.netbeheerders?.join(', ')}</TableCell>
                  <TableCell>{p.aannemers?.join(', ')}</TableCell>
                  <TableCell>{p.statusText}</TableCell>
                  <TableCell>
                    {/* Alleen ontkoppelen als we in het hoofdleiding scherm zitten */}
                    {canKoppelProject && (
                      <StyledIconButton
                        icon={<LinkOff />}
                        title="Ontkoppel dit project"
                        onClick={() =>
                          handleUnlinkClick(p.aanvraagID as string)
                        }
                      />
                    )}
                    {isAuthorizedOnProject && (
                      <StyledIconLink
                        href={`/${
                          /* v8 ignore next - TODO: missing coverage -- @preserve */
                          p.type !== 'Hoofdleiding'
                            ? 'aansluiting'
                            : 'hoofdleiding'
                        }/project/${p.aanvraagID}`}
                        icon={<LaunchIcon />}
                        title={`Navigeer naar project ${p.aanvraagID}`}
                      />
                    )}
                  </TableCell>
                </TableRow>
              )
            }
          })}
      </StyledTable>
      <Dialog open={open}>
        <DialogTitle>Project ontkoppelen</DialogTitle>
        <DialogContent>
          <Typography>Wilt u project {unlinkId} ontkoppelen?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Nee</Button>
          <Button onClick={handleUnlink} variant="contained">
            Ja
          </Button>
        </DialogActions>
      </Dialog>
    </StyledExpandCard>
  )
}
